import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Ban, KeyRound, Trash2, TriangleAlert, Undo2 } from 'lucide-react';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton, useToastStore } from '@/shared/ui';

import type { ImportRollbackBlocker, ImportRunSummary } from '../model/types';
import { useRollbackImport, useRollbackPreview } from '../queries/useImport';
import styles from './Import.module.css';

/**
 * Wycofanie przebiegu (K7, makieta rundy 2 — wariant C1).
 *
 * ⭐⭐ **Dwustopniowe, na widzącego** (decyzja 105): produkt **nie broni się przed adminem**,
 * tylko pokazuje mu rachunek przed kliknięciem. Ale dwóch rzeczy nie zerwie nawet on —
 * zobowiązania (decyzja 22: to dowód wykonanej pracy) i elementu, którego import **nie
 * stworzył**, a tylko adoptował jego klucz (R23).
 *
 * ⛔⛔ **Jedyna nieodwracalna operacja w produkcie**, więc: blokujące potwierdzenie zamiast
 * „Cofnij" w toaście (spec 4B) i **zero optimistic UI** (decyzja 78 — „zrobione" pokazane
 * przedwcześnie znaczyłoby tu stan nie do cofnięcia).
 *
 * ⭐ **„Mimo to zerwij" działa PO POWODZIE, nie po pozycji.** Przy ośmiu tysiącach pozycji
 * „pozycjami" nie istnieje — nikt nie kliknie ośmiu tysięcy razy, więc jedyną realnie używaną
 * drogą byłby przycisk hurtowy na wszystko, czyli najgorsza z możliwych opcji domyślnych.
 * „Zerwij 6 803, które mają tylko właściciela" to zdanie, które admin potrafi ocenić.
 * ⛔ Dlatego przycisku „zerwij wszystko" **nie ma tu w ogóle**.
 */
export function ImportRollback({ run, onBack }: { run: ImportRunSummary; onBack: () => void }) {
  const { t, i18n } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);

  const preview = useRollbackPreview(run.id, true);
  const rollback = useRollbackImport(run.id);

  /** Powody, przy których admin nacisnął „mimo to zerwij". ⚠ Zbiór, nie flaga na wszystko. */
  const [forced, setForced] = useState<readonly string[]>([]);
  const [confirming, setConfirming] = useState(false);

  const format = new Intl.NumberFormat(i18n.language);

  /*
   * ⚠ Zdanie z DWIEMA liczbami nie mieści się w jednym kluczu z `count` — i18next odmienia
   * po jednej. Składamy więc z odmienionych kawałków: „usuń 11 554 elementy, przywróć
   * 1 wartość". Polski ma trzy formy, więc „1 elementów" jest błędem, nie literówką.
   */
  const nElements = (count: number) => t('import.rollback.nElements', { count });
  const nValues = (count: number) => t('import.rollback.nValues', { count });

  if (preview.isPending) {
    return (
      <div className={styles['screen']} aria-busy="true">
        <Skeleton width="20rem" height="2rem" announce />
        <Skeleton height="14rem" />
      </div>
    );
  }

  if (preview.isError) {
    return (
      <div className={styles['screen']}>
        <BackLink onBack={onBack} />
        <div className={styles['failure']} role="alert">
          <p>{describeError(preview.error)}</p>
        </div>
      </div>
    );
  }

  const data = preview.data;

  const forcedCount = data.blockers
    .filter((blocker) => forced.includes(blocker.reasonKey))
    .reduce((sum, blocker) => sum + blocker.count, 0);

  const elementsGone = data.elementsToDelete + forcedCount;

  function toggle(reasonKey: string) {
    setForced((current) =>
      current.includes(reasonKey)
        ? current.filter((key) => key !== reasonKey)
        : [...current, reasonKey],
    );
  }

  return (
    <div className={styles['screen']}>
      <BackLink onBack={onBack} />

      <header className={styles['head']}>
        <h1 className={styles['title']}>
          {t('import.rollback.title', { file: run.fileName ?? t('import.run.noName') })}
        </h1>
        <p className={styles['lead']}>{t('import.rollback.lead')}</p>
      </header>

      <div className={styles['ledger']}>
        <section className={styles['ledgerColumn']} data-tone="undo">
          <h2 className={styles['ledgerHead']}>
            <Undo2 size={15} aria-hidden="true" />
            {t('import.rollback.willUndo')}
          </h2>

          <p className={styles['ledgerSum']}>
            {format.format(elementsGone)}
            {/* ⚠ Rzeczownik odmienia się z liczbą — „1 elementów" to nie literówka, tylko
                brak formy liczebnikowej (polski ma trzy, spec 12B.6). */}
            <span className={styles['ledgerSumHint']}>
              {t('import.rollback.elementsDeleted', { count: elementsGone })}
            </span>
          </p>

          <ul className={styles['ledgerRows']}>
            <LedgerRow label={t('import.rollback.untouched')} count={data.elementsToDelete} />
            <LedgerRow label={t('import.rollback.valuesRestored')} count={data.valuesToRestore} />
            <LedgerRow label={t('import.rollback.relationsRemoved')} count={data.relationsToRemove} />
            <LedgerRow label={t('import.rollback.keysReleased')} count={data.keysToRelease} />
            {/* ⭐ Właściciele przyniesieni przez przebieg — decyzja usera 2026-08-08. Bez tego
                wiersza przebieg, który dopisał 340 właścicieli i nie utworzył ani jednego
                elementu, pokazywałby same zera. */}
            <LedgerRow label={t('import.rollback.ownersRemoved')} count={data.ownersToRemove} />
          </ul>
        </section>

        <section className={styles['ledgerColumn']} data-tone="keep">
          <h2 className={styles['ledgerHead']}>
            <TriangleAlert size={15} aria-hidden="true" />
            {t('import.rollback.willStay')}
          </h2>

          <p className={styles['ledgerSum']}>
            {format.format(Math.max(0, data.kept - forcedCount))}
            <span className={styles['ledgerSumHint']}>
              {t('import.rollback.elementsKept', { count: Math.max(0, data.kept - forcedCount) })}
            </span>
          </p>

          {data.blockers.length === 0 ? (
            <p className={styles['ledgerEmpty']}>{t('import.rollback.nothingBlocks')}</p>
          ) : (
            <ul className={styles['ledgerRows']}>
              {data.blockers.map((blocker) => (
                <BlockerRow
                  key={blocker.reasonKey}
                  blocker={blocker}
                  forced={forced.includes(blocker.reasonKey)}
                  onToggle={() => toggle(blocker.reasonKey)}
                />
              ))}
            </ul>
          )}

          {data.adoptedKeys > 0 ? (
            <p className={styles['ledgerNote']}>
              <KeyRound size={13} aria-hidden="true" />
              {t('import.rollback.adoptedNote', { count: data.adoptedKeys })}
            </p>
          ) : null}
        </section>
      </div>

      <section className={styles['irreversible']}>
        <h2 className={styles['irreversibleTitle']}>
          <TriangleAlert size={16} aria-hidden="true" />
          {t('import.rollback.irreversibleTitle')}
        </h2>

        <p className={styles['irreversibleBody']}>{t('import.rollback.irreversibleBody')}</p>
        <p className={styles['irreversibleBody']}>{t('import.rollback.historyGoes')}</p>

        {/*
          ⭐ Dwa kroki, BEZ wpisywania liczby. Spec 4B każe blokujące potwierdzenie trzymać dla
          rzeczy naprawdę nieodwracalnych — a to nią jest. Wpisywanie dokładałoby rytuał,
          nie zrozumienie: człowiek przepisuje liczbę, zamiast ją przeczytać.
        */}
        {confirming ? (
          <div className={styles['confirmRow']} role="alertdialog" aria-label={t('import.rollback.confirmTitle')}>
            <p className={styles['confirmText']}>
              {t('import.rollback.confirmText', {
                elements: nElements(elementsGone),
                values: nValues(data.valuesToRestore),
              })}
            </p>

            <div className={styles['confirmActions']}>
              <Button variant="ghost" disabled={rollback.isPending} onClick={() => setConfirming(false)}>
                {t('common.action.cancel')}
              </Button>

              <Button
                variant="danger"
                disabled={rollback.isPending}
                onClick={() =>
                  rollback.mutate([...forced], {
                    onSuccess: (result) => {
                      onBack();
                      show({
                        tone: 'success',
                        message: t('import.rollback.done', {
                          elements: nElements(result.elementsDeleted),
                          values: nValues(result.valuesRestored),
                        }),
                      });
                    },
                    onError: (error) => show({ tone: 'danger', message: describeError(error) }),
                  })
                }
              >
                <Trash2 size={15} aria-hidden="true" />
                {rollback.isPending
                  ? t('import.rollback.working')
                  : t('import.rollback.confirmAction')}
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles['confirmActions']}>
            <Button variant="ghost" onClick={onBack}>
              {t('common.action.cancel')}
            </Button>

            <Button variant="danger" disabled={elementsGone === 0} onClick={() => setConfirming(true)}>
              <Trash2 size={15} aria-hidden="true" />
              {t('import.rollback.start', {
                elements: nElements(elementsGone),
                values: nValues(data.valuesToRestore),
              })}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();

  return (
    <Button variant="ghost" onClick={onBack}>
      <ArrowLeft size={15} aria-hidden="true" />
      {t('import.report.back')}
    </Button>
  );
}

function LedgerRow({ label, count }: { label: string; count: number }) {
  const { i18n } = useTranslation();

  // ⚠ Wiersz z zerem ZOSTAJE. Znikający wiersz kazałby zgadywać, czy czegoś nie policzono —
  // a to są liczby, na których stoi decyzja o zerwaniu.
  return (
    <li className={styles['ledgerRow']}>
      <span className={styles['ledgerLabel']}>{label}</span>
      <span className={styles['ledgerCount']}>{new Intl.NumberFormat(i18n.language).format(count)}</span>
    </li>
  );
}

/**
 * Powód, dla którego coś zostaje — z własnym „mimo to zerwij".
 *
 * ⛔ `canForce === false` przy zobowiązaniu i przy adopcji klucza: pierwsze, bo usunięcie
 * elementu z zamkniętym zobowiązaniem kasuje **dowód, że ktoś wykonał pracę** (decyzja 22);
 * drugie, bo import tego elementu **nie stworzył** (R23). Te dwa nie mają przycisku wcale —
 * przycisk wyszarzony byłby obietnicą, że da się go kiedyś kliknąć.
 */
function BlockerRow({
  blocker,
  forced,
  onToggle,
}: {
  blocker: ImportRollbackBlocker;
  forced: boolean;
  onToggle: () => void;
}) {
  const { t, i18n } = useTranslation();

  return (
    <li className={styles['ledgerRow']} data-forced={forced ? 'true' : undefined}>
      <span className={styles['ledgerLabel']}>
        {t(`import.rollback.reason.${blocker.reasonKey}`, { defaultValue: blocker.reasonKey })}
      </span>

      <span className={styles['ledgerCount']}>
        {new Intl.NumberFormat(i18n.language).format(blocker.count)}
      </span>

      {blocker.canForce ? (
        <button type="button" className={styles['forceButton']} aria-pressed={forced} onClick={onToggle}>
          {forced ? t('import.rollback.forced') : t('import.rollback.force')}
        </button>
      ) : (
        <span className={styles['neverBadge']}>
          <Ban size={12} aria-hidden="true" />
          {t('import.rollback.never')}
        </span>
      )}
    </li>
  );
}
